<template>
  <div
    class="zen-search-input"
    :class="{
      'is-focused': isFocused,
      'has-results': filteredResults.length > 0,
      'is-disabled': !isDataLoaded,
      'is-overlay-mode': overlayMode,
      'is-desktop': isDesktop,
      'is-mobile': isMobile,
    }"
  >
    <!-- Backdrop (only shown in desktop mode, mobile has its own backdrop) -->
    <Transition name="backdrop-fade">
      <div
        v-if="
          !overlayMode && showBackdrop && isFocused && (filteredResults.length > 0 || searchQuery)
        "
        class="search-backdrop"
        @click="closeSearch"
      ></div>
    </Transition>

    <!-- Search Input Container -->
    <div class="search-input-container">
      <div class="search-icon">
        <IconRenderer name="search-outline" size="small" />
      </div>

      <input
        v-model="searchQuery"
        ref="searchInputRef"
        type="text"
        :placeholder="$t('placeholder')"
        :disabled="!isDataLoaded"
        class="search-input"
        @focus="handleFocus"
        @blur="handleBlur"
        @keydown="handleKeydown"
      />

      <div class="search-actions">
        <button v-if="searchQuery" class="clear-btn" @click="clearSearch" :title="$t('clear')">
          <IconRenderer name="close-outline" size="small" />
        </button>

        <div class="search-shortcut" v-if="showShortcut && !searchQuery && !isFocused">
          <kbd>{{ isMacOS ? '⌘K' : 'Ctrl+K' }}</kbd>
        </div>
      </div>
    </div>

    <!-- Search Results -->
    <Transition name="results-slide">
      <div class="search-results" v-if="isFocused && filteredResults.length > 0">
        <div class="results-header">
          <span class="results-count">{{ filteredResults.length }} {{ $t('results.found') }}</span>
        </div>

        <div class="results-list">
          <div
            v-for="(result, index) in filteredResults"
            :key="result.id"
            class="result-item"
            :class="{
              'is-highlighted': highlightedIndex === index,
              'is-route': result.type === LinkTypeEnum.Ward,
              'is-link': result.type === LinkTypeEnum.External,
            }"
            @click="handleResultSelect(result)"
            @mouseenter="highlightedIndex = index"
          >
            <div class="result-content">
              <div class="result-title" v-html="highlightMatch(result.title)"></div>
              <div class="result-description" v-if="result.description">
                {{ $t(result.description) }}
              </div>
              <div class="result-path" v-if="result.category">
                {{ $t(result.category) }}
              </div>
            </div>

            <div class="result-meta">
              <div class="result-type" :class="result.type">
                {{ $t(result.type === 'route' ? 'page' : 'link') }}
              </div>

              <div class="result-shortcut" v-if="showShortcut && index < 9">
                <kbd>{{ index + 1 }}</kbd>
              </div>
            </div>
          </div>
        </div>

        <div class="search-footer" v-if="showShortcut">
          <div class="navigation-hints">
            <span class="hint">
              <IconRenderer name="arrow-up-outline" size="small" />
              <IconRenderer name="arrow-down-outline" size="small" />
              {{ $t('navigation') }}
            </span>
            <span class="hint"> <kbd>↵</kbd> {{ $t('select') }} </span>
            <span class="hint"> <kbd>Esc</kbd> {{ $t('close') }} </span>
          </div>
        </div>
      </div>
    </Transition>

    <!-- No Results -->
    <Transition name="results-slide">
      <div class="no-results" v-if="isFocused && searchQuery && filteredResults.length === 0">
        <div class="no-results-icon">
          <IconRenderer name="search-outline" size="large" />
        </div>
        <div class="no-results-content">
          <h4>{{ $t('noResults.title') }}</h4>
          <p>{{ $t('noResults.description') }}</p>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, computed, toRef } from 'vue';
import { useQuickSearch, type SearchResult } from '@/common/composables/useQuickSearch';
import { useLayout } from '@/layouts/composables/useLayout';
import { LAYOUT_CONTEXT } from '@/layouts/constants/layoutContext';
import { LinkTypeEnum } from '@/features/menu-management/constants/menuConstants';
import IconRenderer from '@/common/components/ui/icon/IconRenderer.vue';

interface Props {
  /** Mode overlay (mobile) - force le mode overlay même sur desktop */
  forceOverlayMode?: boolean;
  /** Afficher le backdrop */
  showBackdrop?: boolean;
  /** Activer le raccourci clavier global Cmd+K (auto-activé sur desktop) */
  enableKeyboardShortcut?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  forceOverlayMode: false,
  showBackdrop: true,
  enableKeyboardShortcut: undefined, // undefined = auto-detect based on layout
});

// Layout context detection
const { layoutContext } = useLayout();

// Computed properties based on layout context
const isDesktop = computed(() => layoutContext.value === LAYOUT_CONTEXT.DESKTOP_WEB);
const isMacOS = computed(() => navigator.platform.toUpperCase().includes('MAC'));
const isMobile = computed(
  () =>
    layoutContext.value === LAYOUT_CONTEXT.MOBILE_WEB ||
    layoutContext.value === LAYOUT_CONTEXT.NATIVE_MOBILE
);

// Derived settings from layout context
const overlayMode = computed(() => props.forceOverlayMode || isMobile.value);
const showShortcut = computed(() => isDesktop.value);
const shouldEnableKeyboardShortcut = computed(() =>
  props.enableKeyboardShortcut !== undefined ? props.enableKeyboardShortcut : isDesktop.value
);

const emit = defineEmits<{
  navigate: [result: SearchResult];
  close: [];
  resultSelected: [result: SearchResult];
}>();

// Use the shared search composable
const {
  searchQuery,
  isFocused,
  highlightedIndex,
  searchInputRef,
  filteredResults,
  isDataLoaded,
  handleFocus,
  handleBlur,
  handleKeydown,
  selectResult,
  clearSearch,
  closeSearch,
  highlightMatch,
  focusInput,
  setupKeyboardShortcut,
  cleanupKeyboardShortcut,
} = useQuickSearch({
  onNavigate: (result) => emit('navigate', result),
  enableKeyboardShortcut: shouldEnableKeyboardShortcut.value,
});

// Handle result selection with emit
function handleResultSelect(result: SearchResult) {
  selectResult(result);
  emit('resultSelected', result);
}

// Expose focus method for parent components
defineExpose({
  focusInput,
  closeSearch,
  searchInputRef,
});

onMounted(() => {
  if (shouldEnableKeyboardShortcut.value) {
    setupKeyboardShortcut();
  }
});

onUnmounted(() => {
  if (shouldEnableKeyboardShortcut.value) {
    cleanupKeyboardShortcut();
  }
});
</script>

<style scoped>
.zen-search-input {
  position: relative;
  width: 100%;
}

/* Backdrop */
.search-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 99;
}

.backdrop-fade-enter-active,
.backdrop-fade-leave-active {
  transition: opacity 0.2s ease;
}

.backdrop-fade-enter-from,
.backdrop-fade-leave-to {
  opacity: 0;
}

/* Input Container */
.search-input-container {
  position: relative;
  display: flex;
  align-items: center;
  background: var(--ion-background-color);
  border: 1px solid var(--ion-input-border-color);
  border-radius: 35px;
  transition: all 0.3s ease;
  overflow: hidden;
  z-index: 100;
}

.zen-search-input.is-focused .search-input-container {
  border-color: var(--ion-input-border-color);
  background: var(--ion-background-color);
  box-shadow: var(--ion-box-shadow-hover);
}

/* Disabled State */
.zen-search-input.is-disabled .search-input-container {
  opacity: 0.5;
  cursor: not-allowed;
}

.zen-search-input.is-disabled .search-input {
  cursor: not-allowed;
}

.zen-search-input.is-disabled .search-icon {
  opacity: 0.5;
}

/* Overlay mode adjustments */
.zen-search-input.is-overlay-mode {
  display: flex;
  flex-direction: column-reverse;
}

.zen-search-input.is-overlay-mode .search-input-container {
  border-radius: 12px;
}

.search-icon {
  padding: 12px 16px;
  color: var(--ion-color-medium);
  display: flex;
  align-items: center;
}

.search-input {
  flex: 1;
  border: none;
  background: transparent;
  padding: 12px 0;
  font-size: 14px;
  color: var(--ion-text-color);
  outline: none;
}

.search-input::placeholder {
  color: var(--ion-color-medium);
}

.search-actions {
  padding: 8px 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.clear-btn {
  background: transparent;
  border: none;
  color: var(--ion-color-medium);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.clear-btn:hover {
  background: rgba(var(--ion-color-medium-rgb), 0.1);
  color: var(--ion-text-color);
}

.search-shortcut {
  display: flex;
  align-items: center;
}

kbd {
  background: rgba(var(--ion-color-medium-rgb), 0.1);
  border: 1px solid rgba(var(--ion-border-color-rgb), 0.3);
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 11px;
  font-family: monospace;
  color: var(--ion-color-medium);
}

/* Search Results */
.search-results {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  background: var(--ion-background-color);
  border: 1px solid rgba(var(--ion-border-color-rgb), 0.2);
  border-radius: 16px;
  box-shadow: var(--ion-box-shadow-hover);
  z-index: 100;
  max-height: 400px;
  overflow: hidden;
}

/* Overlay mode results */
.zen-search-input.is-overlay-mode .search-results {
  position: relative;
  top: auto;
  bottom: auto;
  margin-bottom: 0;
  margin-top: 12px;
  border-radius: 12px;
  box-shadow: none;
  border: 1px solid var(--ion-input-border-color);
}

.results-slide-enter-active,
.results-slide-leave-active {
  transition: all 0.2s ease;
}

.results-slide-enter-from,
.results-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* Overlay mode: results slide from top */
.zen-search-input.is-overlay-mode .results-slide-enter-from,
.zen-search-input.is-overlay-mode .results-slide-leave-to {
  transform: translateY(8px);
}

.results-header {
  padding: 12px 16px;
  border-bottom: 1px solid rgba(var(--ion-border-color-rgb), 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.results-count {
  font-size: 12px;
  color: var(--ion-color-medium);
  font-weight: 500;
}

.results-list {
  max-height: 280px;
  overflow-y: auto;
  padding: 4px 0;
}

/* Overlay mode results list */
.zen-search-input.is-overlay-mode .results-list {
  max-height: calc(100vh - 200px);
}

.result-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin: 2px 8px;
  border-radius: 8px;
}

.result-item:hover,
.result-item.is-highlighted {
  background: rgba(var(--ion-color-primary-rgb), 0.05);
}

.result-item.is-highlighted {
  background: rgba(var(--ion-color-primary-rgb), 0.08);
}

.result-content {
  flex: 1;
  min-width: 0;
}

.result-title {
  font-size: 14px;
  font-weight: 400;
  color: var(--ion-text-color);
  margin-bottom: 2px;
}

.result-title :deep(mark) {
  background: var(--ion-color-primary);
  color: var(--ion-text-color-contrast);
  padding: 1px 2px;
  border-radius: 2px;
}

.result-description {
  font-size: 12px;
  color: var(--ion-color-medium);
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.result-path {
  font-size: 11px;
  color: var(--ion-color-medium);
  opacity: 0.8;
}

.result-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.result-type {
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.result-type.route {
  background: rgba(var(--ion-color-primary-rgb), 0.1);
  color: var(--ion-color-primary);
}

.result-type.link {
  background: rgba(var(--ion-color-secondary-rgb), 0.1);
  color: var(--ion-color-secondary);
}

.result-shortcut kbd {
  background: rgba(var(--ion-color-medium-rgb), 0.1);
  border: 1px solid rgba(var(--ion-border-color-rgb), 0.2);
  padding: 1px 4px;
  font-size: 10px;
}

.search-footer {
  padding: 8px 16px;
  border-top: 1px solid rgba(var(--ion-border-color-rgb), 0.1);
  background: rgba(var(--ion-color-light-rgb), 0.3);
}

.navigation-hints {
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: center;
}

.hint {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: var(--ion-text-color);
}

.hint kbd {
  margin: 0 2px;
  color: var(--ion-text-color);
  background-color: var(--ion-background-color-primary);
}

/* No Results */
.no-results {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  background: var(--ion-background-color);
  border: 1px solid var(--ion-input-border-color);
  border-radius: 16px;
  box-shadow: var(--ion-box-shadow-hover);
  z-index: 100;
  padding: 24px;
  text-align: center;
}

/* Overlay mode no results */
.zen-search-input.is-overlay-mode .no-results {
  position: relative;
  top: auto;
  bottom: auto;
  margin-bottom: 0;
  margin-top: 12px;
  border-radius: 12px;
  box-shadow: none;
}

.no-results-icon {
  margin-bottom: 12px;
  color: var(--ion-color-medium);
  opacity: 0.5;
}

.no-results-content h4 {
  margin: 0 0 8px 0;
  font-size: 14px;
  color: var(--ion-text-color);
}

.no-results-content p {
  margin: 0;
  font-size: 12px;
  color: var(--ion-color-medium);
}

/* Scrollbar styling */
.results-list::-webkit-scrollbar {
  width: 6px;
}

.results-list::-webkit-scrollbar-track {
  background: transparent;
}

.results-list::-webkit-scrollbar-thumb {
  background: rgba(var(--ion-color-medium-rgb), 0.3);
  border-radius: 3px;
}

.results-list::-webkit-scrollbar-thumb:hover {
  background: rgba(var(--ion-color-medium-rgb), 0.5);
}
</style>
